import React, { useEffect, useContext, useState } from "react";
import { Stack, Flex, Box, Text, VisuallyHidden, ControlBox, Icon, Divider } from "@chakra-ui/core";
import { formatPrice } from "utils/shared/prices";
import setItemsUnchecked from "utils/shared/setItemsUnchecked";
import { ContextStore } from "context/ContextStore";

const FoodOptions = () => {
  const { currentOrder, setCurrentOrder } = useContext(ContextStore);
  const [selectedOptions, setSelectedOptions] = useState([...currentOrder.options]);

  // Controller for the radio buttons on FoodOptions
  function checkOption(e, i, j) {
    const selectedOptionsCopy = [...selectedOptions];

    // Only if it's a radio input
    if (selectedOptionsCopy[i].min === 1 && selectedOptionsCopy[i].max === 1) {
      setItemsUnchecked(selectedOptionsCopy[i].optionItems);
    }

    selectedOptionsCopy[i].optionItems[j].checked = e.target.checked;
    setSelectedOptions(selectedOptionsCopy);
    setCurrentOrder({ ...currentOrder, options: selectedOptions });
  }

  // Controller for the qty-limited checkboxes on FoodOptions
  const disableCheckbox = (i, j) => {
    if (currentOrder.options[i].optionItems[j].isAvailable === false) {
      return true;
    }
    const totalSelectedItems = currentOrder.options[i].optionItems.filter((item) => item.checked).length;
    if (totalSelectedItems < currentOrder.options[i].max && totalSelectedItems >= currentOrder.options[i].min) {
      return false;
    } else if (currentOrder.options[i].optionItems[j].checked === true) {
      return false;
    } else if (totalSelectedItems === 0) {
      return false;
    } else {
      return true;
    }
  };

  return (
    <>
      {currentOrder?.options.map((option, i) => (
        <Box key={i} pt={i === 0 ? "0" : "5"}>
          <Box bg="gray.50" w="full" mb="4" px="6" py="4">
            <Text fontSize="md" fontWeight="bold" textTransform="uppercase" color="gray.900">
              {option.name}
              <Box as="span" textTransform="uppercase" fontSize="xs" fontWeight="medium" color="gray.600" pl="2">
                {option.min > 0 && option.max === 1 ? "Obligatoire" : null}
                {option.min > 0 && option.max !== 1 ? `Obligatoire - ${option.max} max.` : null}
                {option.min === 0 ? `Optionnel - ${option.max} max.` : null}
              </Box>
            </Text>
            {option.description && (
              <Text fontSize="sm" fontWeight="medium" color="gray.600" pt="1">
                {option.description}
              </Text>
            )}
          </Box>
          <Stack variantColor="green" w="full" px="6">
            {/* RENDER RADIO INPUTS */}
            {option.min === 1 && option.max === 1
              ? option.optionItems.map((item, j) => {
                  return (
                    <React.Fragment key={j}>
                      {j !== 0 && <Divider />}
                      <Flex
                        as="label"
                        // pt={j === 0 ? "0" : 1}
                        py="1"
                        w="full"
                        verticalAlign="baseline"
                        align="center"
                      >
                        {/* This is the sibling input, it's visually hidden */}
                        <VisuallyHidden
                          disabled={!item.isAvailable}
                          as="input"
                          type="radio"
                          value={item.checked}
                          name={item.name}
                          id={item.name}
                          checked={selectedOptions[i].optionItems[j].checked || false}
                          onChange={(e) => checkOption(e, i, j)}
                        />

                        {/* This is the control box with a check icon as children */}
                        <ControlBox
                          justifySelf="start"
                          borderWidth="2px"
                          size="16px"
                          rounded="full"
                          type="radio"
                          borderColor="gray.200"
                          _checked={{ bg: "green.500", color: "white", borderColor: "green.500" }}
                          _focus={{ borderColor: "green.600", boxShadow: "outline" }}
                        >
                          <Icon name="check" size="10px" />
                        </ControlBox>

                        {/* You can pass additional text */}
                        <Flex flexGrow="1" ml={2} flexDirection="column" align="flex-start" justify="center">
                          <Text
                            color={!item.isAvailable ? "gray.400" : "gray.700"}
                            fontSize="md"
                            as="span"
                            fontWeight={500}
                          >
                            {item.name} {!item.isAvailable && "(Indisponible)"}
                          </Text>
                          {item.description && (
                            <Text
                              color={!item.isAvailable ? "gray.400" : "gray.500"}
                              fontSize="sm"
                              as="span"
                              fontWeight={500}
                            >
                              {item.description}
                            </Text>
                          )}
                        </Flex>

                        {item.price !== 0 && (
                          <Box
                            as="span"
                            flexShrink="0"
                            fontSize="md"
                            color={!item.isAvailable ? "gray.400" : "gray.600"}
                          >
                            ({item.price > 0 && "+"} {formatPrice(item.price)})
                          </Box>
                        )}
                      </Flex>
                    </React.Fragment>
                  );
                })
              : null}
            {/* RENDER CHECKBOXES */}
            {option.min !== 1 || option.max !== 1
              ? option.optionItems.map((item, j) => (
                  <React.Fragment key={j}>
                    {j !== 0 && <Divider />}
                    <Flex
                      as="label"
                      // pt={j === 0 ? "0" : 1}
                      py="1"
                      w="full"
                      verticalAlign="baseline"
                      align="center"
                    >
                      {/* This is the sibling input, it's visually hidden */}
                      <VisuallyHidden
                        as="input"
                        type="checkbox"
                        value={item.checked}
                        checked={selectedOptions[i].optionItems[j].checked || false}
                        onChange={(e) => checkOption(e, i, j)}
                        disabled={disableCheckbox(i, j)}
                      />

                      {/* This is the control box with a check icon as children */}
                      <ControlBox
                        justifySelf="start"
                        borderWidth="2px"
                        size="16px"
                        rounded="sm"
                        borderColor="gray.200"
                        _disabled={{ bg: "gray.100", borderColor: "gray.100" }}
                        _checked={{ bg: "green.500", color: "white", borderColor: "green.500" }}
                        _focus={{ borderColor: "green.600", boxShadow: "outline" }}
                      >
                        <Icon name="check" size="10px" />
                      </ControlBox>

                      {/* You can pass additional text */}
                      <Flex flexGrow="1" ml={2} flexDirection="column" align="flex-start" justify="center">
                        <Text
                          color={disableCheckbox(i, j) ? "gray.400" : "gray.700"}
                          fontSize="md"
                          as="span"
                          fontWeight={500}
                        >
                          {item.name} {!item.isAvailable && "(Indisponible)"}
                        </Text>
                        {item.description && (
                          <Text
                            color={disableCheckbox(i, j) ? "gray.400" : "gray.500"}
                            fontSize="sm"
                            as="span"
                            fontWeight={500}
                          >
                            {item.description}
                          </Text>
                        )}
                      </Flex>

                      {item.price !== 0 && (
                        <Box as="span" fontSize="md" color={disableCheckbox(i, j) ? "gray.400" : "gray.600"}>
                          ({item.price > 0 && "+"} {formatPrice(item.price)})
                        </Box>
                      )}
                    </Flex>
                  </React.Fragment>
                ))
              : null}
          </Stack>
        </Box>
      ))}
    </>
  );
};

export default FoodOptions;
